/**
 * React Query wrappers for the balance-alert operator surface (#1892).
 *
 * Query keys
 *   - `['account-balance-alerts']`                  — root; every mutation invalidates it
 *   - `['account-balance-alerts', 'settings']`      — the global switch
 *   - `['account-balance-alerts', 'preview']`       — who the NEXT pass would mail
 *   - `['account-balance-alerts', 'history']`       — what was already mailed
 *   - `['account-balance-alerts', 'suppressions']`  — the operator mutes
 *
 * The preview is a QUERY here, unlike #1877's recipient preview which is a
 * mutation. The difference is what a refetch would destroy: there, a refresh
 * replaces a candidate list the operator has been striking rows out of. Here
 * the preview carries no operator-local state at all — it is a read of the
 * live population — so a refetch after a mute lands is the CORRECT behaviour
 * rather than a hazard, and it is exactly what makes the `suppressed` column
 * update the moment somebody presses the button.
 */
import type {
  AccountBalanceAlertHistoryEntry,
  AccountBalanceAlertPreviewResponse,
  AccountBalanceAlertSettings,
  AccountBalanceAlertSuppression,
  CreateAccountBalanceAlertSuppressionResponse,
  DeleteAccountBalanceAlertSuppressionResponse,
  UpdateAccountBalanceAlertSettingsRequest,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  createAccountBalanceAlertSuppression,
  deleteAccountBalanceAlertSuppression,
  getAccountBalanceAlertPreview,
  getAccountBalanceAlertSettings,
  listAccountBalanceAlertHistory,
  listAccountBalanceAlertSuppressions,
  updateAccountBalanceAlertSettings,
} from './accountBalanceAlerts.ts';

export const ACCOUNT_BALANCE_ALERTS_KEY = ['account-balance-alerts'] as const;

function invalidateAll(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ACCOUNT_BALANCE_ALERTS_KEY });
}

export function useAccountBalanceAlertSettings(): UseQueryResult<AccountBalanceAlertSettings> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ACCOUNT_BALANCE_ALERTS_KEY, 'settings'],
    queryFn: async ({ signal }) =>
      (await getAccountBalanceAlertSettings(fetchImpl, signal)).settings,
  });
}

/**
 * Moving the switch invalidates the PREVIEW too, not only the settings.
 *
 * `AccountBalanceAlertPreviewResponse.alerts_enabled` is a copy of the switch,
 * and it is the copy the population table reads its "these would actually be
 * mailed" banner from. Left stale, opening the brake would leave a list of
 * twelve accounts still captioned as unreachable.
 */
export function useUpdateAccountBalanceAlertSettings(): UseMutationResult<
  AccountBalanceAlertSettings,
  Error,
  UpdateAccountBalanceAlertSettingsRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateAccountBalanceAlertSettingsRequest) =>
      (await updateAccountBalanceAlertSettings(body, fetchImpl)).settings,
    onSuccess: (settings) => {
      qc.setQueryData([...ACCOUNT_BALANCE_ALERTS_KEY, 'settings'], settings);
      invalidateAll(qc);
    },
  });
}

export function useAccountBalanceAlertPreview(): UseQueryResult<AccountBalanceAlertPreviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ACCOUNT_BALANCE_ALERTS_KEY, 'preview'],
    queryFn: ({ signal }) => getAccountBalanceAlertPreview(fetchImpl, signal),
  });
}

export function useAccountBalanceAlertHistory(): UseQueryResult<
  readonly AccountBalanceAlertHistoryEntry[]
> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ACCOUNT_BALANCE_ALERTS_KEY, 'history'],
    queryFn: async ({ signal }) => (await listAccountBalanceAlertHistory(fetchImpl, signal)).entries,
  });
}

export function useAccountBalanceAlertSuppressions(): UseQueryResult<
  readonly AccountBalanceAlertSuppression[]
> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ACCOUNT_BALANCE_ALERTS_KEY, 'suppressions'],
    queryFn: async ({ signal }) =>
      (await listAccountBalanceAlertSuppressions(fetchImpl, signal)).suppressions,
  });
}

export function useCreateAccountBalanceAlertSuppression(): UseMutationResult<
  CreateAccountBalanceAlertSuppressionResponse,
  Error,
  { readonly accountId: string; readonly reason: string | null }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, reason }) =>
      createAccountBalanceAlertSuppression(accountId, { reason }, fetchImpl),
    onSuccess: () => {
      invalidateAll(qc);
    },
  });
}

export function useDeleteAccountBalanceAlertSuppression(): UseMutationResult<
  DeleteAccountBalanceAlertSuppressionResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => deleteAccountBalanceAlertSuppression(accountId, fetchImpl),
    onSuccess: () => {
      invalidateAll(qc);
    },
  });
}
